import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { emojiActions, type EmojiType } from '@/store/slices/emojiSlice';
import { sendUpdateEmojiUserList } from '@/utils/conferenceUtils';
import { EMOTICON_TYPE, EMOJI_TAB, EMOJI_COLOR } from './ReactionButton/emoticonTypes';
import { emojiData } from './ReactionButton/EmojiData';
import EmojiItem from './ReactionButton/EmojiItem';
import CharacterEmoticon from './ReactionButton/CharacterEmoticon';

/**
 * 퀵 리액션 리스트 (SVG 아이콘 연동)
 */
const QUICK_EMOJI_LIST: { type: EmojiType; icon: string; label: string }[] = [
  { type: 'THUMBS_UP', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-thumbs-up.svg', label: '좋아요' },
  { type: 'CLAP_HAND', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-clapping-hands.svg', label: '박수' },
  { type: 'OK', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-ok-hand.svg', label: 'OK' },
  { type: 'SMILE_WITH_HEART_EYES', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-smiling-face-with-heart-eyes.svg', label: '하트눈' },
  { type: 'FIRE', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-fire.svg', label: '불꽃' },
  { type: 'PARTY_POPPER', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-party-popper.svg', label: '축하' },
  { type: 'TWO_HEARTS', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-two-hearts.svg', label: '하트' },
  { type: 'HUNDRED_POINT', icon: 'https://static.wehago.com/imgs/rtc/reaction/emoji-hundred-points.svg', label: '100점' },
];

const RECENT_EMOJI_KEY = 'wetalk_emoji';

const ReactionButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const emojiUserList = useAppSelector((s) => s.emoji.emojiUserList);
  
  // 상태 관리
  const [isReactionBarOpen, setIsReactionBarOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<EMOTICON_TYPE>(EMOTICON_TYPE.CHARACTER);
  const [currentEmojiTab, setCurrentEmojiTab] = useState<EMOJI_TAB>(EMOJI_TAB.PEOPLE);
  const [recentEmojiUnicodes, setRecentEmojiUnicodes] = useState<string[]>([]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const emojiScrollRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  // 초기 로드: 최근 사용 이모지
  useEffect(() => {
    const saved = localStorage.getItem(RECENT_EMOJI_KEY);
    if (saved) {
      try {
        setRecentEmojiUnicodes(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse recent emojis', e);
      }
    }
  }, []);

  // 리액션 레이어 위치 측정 → 이모지가 레이어에 가려지지 않도록 bottom 값 Redux에 저장
  useEffect(() => {
    if (isReactionBarOpen) {
      requestAnimationFrame(() => {
        if (layerRef.current) {
          const rect = layerRef.current.getBoundingClientRect();
          const distFromBottom = window.innerHeight - rect.top + 16;
          dispatch(emojiActions.setReactionLayerBottom(distFromBottom));
        }
      });
    } else {
      dispatch(emojiActions.setReactionLayerBottom(0));
    }
  }, [isReactionBarOpen]);

  // 외부 클릭 및 ESC 닫기
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsReactionBarOpen(false);
        setIsPaletteOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsReactionBarOpen(false);
        setIsPaletteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 리액선 전송 및 저장 공통 로직
  const handleAction = useCallback((emoji: string, type: 'emoji' | 'character' = 'emoji') => {
    const now = Date.now();
    const effectiveUserId = local.wehagoId || local.userId; // Portal ID 사용 (Jitsi ID는 휘발성)
    console.log(`[ReactionButton] handleAction ID=${effectiveUserId} type=${type} emoji=${emoji}`);

    const emojiItem = {
      id: `${effectiveUserId}-${now}`,
      userId: effectiveUserId,
      userName: local.userName || local.nickname || local.userId,
      emoji: emoji,
      timestamp: now,
      type: type as 'emoji' | 'character'
    };

    dispatch(emojiActions.addEmoji(emojiItem));

    // 다른 참여자들에게 브로드캐스트
    const updatedEmojiList = [...emojiUserList, {
      emoji: emojiItem.emoji,
      userId: emojiItem.userId,
      userName: emojiItem.userName,
      timestamp: emojiItem.timestamp,
      type: emojiItem.type
    }];
    sendUpdateEmojiUserList(updatedEmojiList);

    // 팔레트 닫기
    setIsPaletteOpen(false);
    setIsReactionBarOpen(false);

    // 로컬스토리지 저장 (이모지인 경우)
    if (type === 'emoji') {
       let newRecent = [emoji, ...recentEmojiUnicodes.filter(u => u !== emoji)];
       if (newRecent.length > 18) newRecent = newRecent.slice(0, 18);
       setRecentEmojiUnicodes(newRecent);
       localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(newRecent));
    }
  }, [dispatch, local, emojiUserList, recentEmojiUnicodes]);

  // 이모지 대분류별 렌더링
  const renderEmojiList = (categoryName: string) => {
    let icons = [];
    if (categoryName.toLowerCase() === 'recently') {
      icons = recentEmojiUnicodes.flatMap(unicode => {
        const found = emojiData.find(e => e.unified.toLowerCase() === unicode.toLowerCase());
        return found ? [found] : [];
      });
    } else {
      icons = emojiData
        .filter(e => e.category.toLowerCase() === categoryName.toLowerCase())
        .sort((a, b) => a.sort_order - b.sort_order);
    }

    return icons.map((icon, idx) => (
      <EmojiItem
        key={`${categoryName}_${icon.unified}`}
        iconClass={`emoji_${icon.unified.toLowerCase()}`}
        iconColor={EMOJI_COLOR[idx % EMOJI_COLOR.length]}
        iconShortName={icon.short_name}
        onMouseDown={(e) => e.preventDefault()}
        onMouseUp={() => handleAction(icon.unified.toLowerCase(), 'emoji')}
      />
    ));
  };

  const handleEmojiTabClick = (tab: EMOJI_TAB, categoryId: string) => {
    setCurrentEmojiTab(tab);
    const target = document.getElementById(`reaction_emoji_category_${tab}`);
    if (target && emojiScrollRef.current) {
      const top = target.offsetTop - 44;
      emojiScrollRef.current.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div ref={containerRef} className={`newHeader_btnItem${isReactionBarOpen ? ' open' : ''}`}>
      <button 
        type="button" 
        className="newHeader_btn" 
        onClick={() => setIsReactionBarOpen(!isReactionBarOpen)}
      >
        <span className={`newHeader_btnIcon icoReaction${isReactionBarOpen ? ' active' : ''}`} />
        <span className="newHeader_btnText">리액션</span>
      </button>

      {/* [D] 퀵 리액션 바 */}
      <div ref={layerRef} className={`funcBtnBox__layer funcBtnBox__layer--reaction ${isReactionBarOpen ? 'active' : ''}`}>
        <ul className="funcBtnBox__layerList">
          {QUICK_EMOJI_LIST.map((emoji) => (
            <li key={emoji.type}>
              <button 
                type="button" 
                className="emoji_item gray" 
                onClick={() => handleAction(emoji.type, 'emoji')}
                title={emoji.label}
              >
                <img src={emoji.icon} alt={emoji.label} className="emoji_itemImage" />
              </button>
            </li>
          ))}
          {/* <li>
            <button 
              type="button" 
              className="emoji_moreBtn" 
              onClick={() => setIsPaletteOpen(!isPaletteOpen)}
            >
              <span className="blind">더보기</span>
            </button>
          </li> */}
        </ul>

        {/* [D] 전체 이모티콘 레이어 */}
        {isPaletteOpen && (
          <div className="emojiLayerBox">
            <span className="sp_co arr_down"></span>
            <div className="wehago_emotion">
              <ul className="wehago_emotion_tabbox">
                <li className={activeTab === EMOTICON_TYPE.CHARACTER ? 'selected' : ''}>
                  <button type="button" onClick={() => setActiveTab(EMOTICON_TYPE.CHARACTER)}>캐릭터</button>
                </li>
                <li className={activeTab === EMOTICON_TYPE.EMOJI ? 'selected' : ''}>
                  <button type="button" onClick={() => setActiveTab(EMOTICON_TYPE.EMOJI)}>이모티콘</button>
                </li>
              </ul>

              {/* 캐릭터 탭 */}
              {activeTab === EMOTICON_TYPE.CHARACTER && (
                <div className="character_content open">
                  <CharacterEmoticon 
                    callComponent="reaction" 
                    onSend={(code) => handleAction(code, 'character')} 
                  />
                </div>
              )}

              {/* 이모티콘 탭 */}
              {activeTab === EMOTICON_TYPE.EMOJI && (
                <div className="emoji_content open">
                  <div className="emoji_header" id="reaction_emoji_header">
                    {recentEmojiUnicodes.length > 0 && (
                      <a className={`emoji_tab ${currentEmojiTab === EMOJI_TAB.RECENT ? 'active' : ''}`} 
                         onClick={() => handleEmojiTabClick(EMOJI_TAB.RECENT, 'recent')}>
                        <span className="emoji_outer emoji_sizer"><span className="emoji_inner emoji_1f552" /></span>
                      </a>
                    )}
                    <a className={`emoji_tab ${currentEmojiTab === EMOJI_TAB.PEOPLE ? 'active' : ''}`} 
                       onClick={() => handleEmojiTabClick(EMOJI_TAB.PEOPLE, 'people')}>
                      <span className="emoji_outer emoji_sizer"><span className="emoji_inner emoji_1f60e" /></span>
                    </a>
                    <a className={`emoji_tab ${currentEmojiTab === EMOJI_TAB.NATURE ? 'active' : ''}`} 
                       onClick={() => handleEmojiTabClick(EMOJI_TAB.NATURE, 'nature')}>
                      <span className="emoji_outer emoji_sizer"><span className="emoji_inner emoji_2618" /></span>
                    </a>
                    <a className={`emoji_tab ${currentEmojiTab === EMOJI_TAB.FOODS ? 'active' : ''}`} 
                       onClick={() => handleEmojiTabClick(EMOJI_TAB.FOODS, 'foods')}>
                      <span className="emoji_outer emoji_sizer"><span className="emoji_inner emoji_1f355" /></span>
                    </a>
                    <a className={`emoji_tab ${currentEmojiTab === EMOJI_TAB.ACTIVITY ? 'active' : ''}`} 
                       onClick={() => handleEmojiTabClick(EMOJI_TAB.ACTIVITY, 'activity')}>
                      <span className="emoji_outer emoji_sizer"><span className="emoji_inner emoji_1f3c8" /></span>
                    </a>
                  </div>
                  <div className="emoji_scroll_wrapper" ref={emojiScrollRef} style={{ overflowY: 'auto' }}>
                    <div className="emoji_sections">
                      {recentEmojiUnicodes.length > 0 && (
                        <div className="emoji_sections">
                          <h3 id="reaction_emoji_category_recent">Recently Used</h3>
                          <div className="emoji_list">{renderEmojiList('recently')}</div>
                        </div>
                      )}
                      <div className="emoji_sections">
                        <h3 id="reaction_emoji_category_people">People</h3>
                        <div className="emoji_list">{renderEmojiList('People')}</div>
                      </div>
                      <div className="emoji_sections">
                        <h3 id="reaction_emoji_category_nature">Nature</h3>
                        <div className="emoji_list">{renderEmojiList('Nature')}</div>
                      </div>
                      <div className="emoji_sections">
                        <h3 id="reaction_emoji_category_foods">Foods</h3>
                        <div className="emoji_list">{renderEmojiList('Foods')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReactionButton;
